import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import {
    getDirectoryAccess,
    saveDirectoryAccess,
    getDirectoryAccessSubjects,
} from '../../../services/api';
import {
    AlertTriangle,
    Check,
    ChevronDown,
    ChevronRight,
    Folder,
    Loader2,
    Lock,
    Plus,
    Shield,
    ShieldAlert,
    Trash2,
    Unlock,
    Users,
    X,
} from 'lucide-react';

const PERM_KEYS = ['canView', 'canUpdate', 'canDelete', 'canCreateChildren'];
const PERM_LABELS = { canView: 'Voir', canUpdate: 'Modifier', canDelete: 'Supprimer', canCreateChildren: 'Créer enfant' };

function PermBadge({ granted }) {
    return granted ? (
        <Check size={14} className="text-emerald-600" />
    ) : (
        <X size={14} className="text-stone-300" />
    );
}

function GrantSubjectLabel({ grant }) {
    if (grant.grantType === 'user') {
        return <span className="font-medium text-stone-800">{grant.targetName}</span>;
    }
    if (grant.grantType === 'group') {
        return (
            <span className="font-medium text-stone-800">
                <span className="badge badge-info badge-sm mr-1">Groupe</span>
                {grant.targetName}
                {grant.groupTypeLabel && (
                    <span className="text-slate-500 text-xs ml-1">· {grant.groupTypeLabel}</span>
                )}
            </span>
        );
    }
    if (grant.grantType === 'wiki_role') {
        return (
            <span className="font-medium text-stone-800">
                <span className="text-teal-600 text-xs mr-1">Wiki</span>
                {grant.roleName || grant.roleId}
                {grant.wikiGroupName && (
                    <span className="text-stone-400 text-xs ml-1">({grant.wikiGroupName})</span>
                )}
            </span>
        );
    }
    return (
        <span className="font-medium text-stone-800">
            <span className="text-iris-600 text-xs mr-1">Rôle</span>
            {grant.roleName || grant.roleId}
            {grant.projectName && (
                <span className="text-stone-400 text-xs ml-1">({grant.projectName})</span>
            )}
        </span>
    );
}

function InheritanceBadge({ inherited }) {
    return inherited ? (
        <span className="text-3xs px-1.5 py-0.5 rounded bg-stone-100 text-stone-500 font-medium">Hérité</span>
    ) : (
        <span className="text-3xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">Local</span>
    );
}

function ChildStatusBadge({ child }) {
    if (child.overrideAccess) {
        return (
            <span className="inline-flex items-center gap-1 text-2xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium">
                <Lock size={10} /> Héritage coupé
            </span>
        );
    }
    if (child.hasLocalGrants) {
        return (
            <span className="inline-flex items-center gap-1 text-2xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">
                <Shield size={10} /> {child.localGrantCount} grant{child.localGrantCount > 1 ? 's' : ''} local{child.localGrantCount > 1 ? 'aux' : ''}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 text-2xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">
            <Unlock size={10} /> Hérite du parent
        </span>
    );
}

export default function DocAccessPanel({ directoryId, isActive, onSaved, readOnly: readOnlyProp = false }) {
    const fieldId = useId();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [accessDenied, setAccessDenied] = useState(false);
    const [backendReadOnly, setBackendReadOnly] = useState(false);
    const readOnly = readOnlyProp || backendReadOnly;

    const [info, setInfo] = useState(null);
    const [localGrants, setLocalGrants] = useState([]);
    const [overrideAccess, setOverrideAccess] = useState(false);

    const [subjects, setSubjects] = useState(null);
    const [subjectsLoading, setSubjectsLoading] = useState(false);

    const [showAddForm, setShowAddForm] = useState(false);
    const [addType, setAddType] = useState('user');
    const [addTargetId, setAddTargetId] = useState('');
    const [addRoleId, setAddRoleId] = useState('');
    const [addPerms, setAddPerms] = useState({ canView: true, canUpdate: false, canDelete: false, canCreateChildren: false });

    const [childrenOpen, setChildrenOpen] = useState(false);

    const loadData = useCallback(async () => {
        if (!directoryId) return;
        setLoading(true);
        setError('');
        setAccessDenied(false);
        try {
            const data = await getDirectoryAccess(directoryId);
            setInfo(data);
            setLocalGrants(data.localGrants || []);
            setOverrideAccess(data.overrideAccess || false);
            if (data.readOnly != null) setBackendReadOnly(!!data.readOnly);
        } catch (err) {
            if (err.status === 403) {
                setAccessDenied(true);
            } else {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    }, [directoryId]);

    useEffect(() => {
        if (isActive && directoryId) loadData();
    }, [isActive, directoryId, loadData]);

    const loadSubjects = useCallback(async () => {
        if (subjects || subjectsLoading || !directoryId) return;
        setSubjectsLoading(true);
        try {
            const data = await getDirectoryAccessSubjects(directoryId);
            setSubjects(data);
        } catch {
            setSubjects({ users: [], groupTypes: [], projectMembers: [], availableRoles: [] });
        } finally {
            setSubjectsLoading(false);
        }
    }, [directoryId, subjects, subjectsLoading]);

    const handleToggleLocalPerm = useCallback((idx, perm) => {
        setLocalGrants((prev) => prev.map((g, i) => (i !== idx ? g : { ...g, [perm]: !g[perm] })));
    }, []);

    const handleRemoveLocal = useCallback((idx) => {
        setLocalGrants((prev) => prev.filter((_, i) => i !== idx));
    }, []);

    const handleAddGrant = useCallback(() => {
        if (!addTargetId) return;
        const newGrant = {
            grantType: addType,
            targetId: parseInt(addTargetId, 10),
            targetName: '',
            roleId: addType === 'project_role' ? addRoleId : '',
            roleName: '',
            projectName: '',
            wikiGroupName: '',
            groupTypeLabel: '',
            ...addPerms,
            isInherited: false,
        };
        if (addType === 'user' && subjects?.users) {
            const u = subjects.users.find((s) => String(s.id) === String(newGrant.targetId));
            if (u) newGrant.targetName = u.name;
        }
        if (addType === 'project_role') {
            newGrant.targetName = info?.projectName || '';
            newGrant.projectName = info?.projectName || '';
            if (subjects?.availableRoles) {
                const r = subjects.availableRoles.find((s) => String(s.id) === String(addRoleId));
                if (r) newGrant.roleName = r.label;
            }
        }
        if (addType === 'group') {
            for (const type of subjects?.groupTypes || []) {
                const g = type.groups.find((s) => String(s.id) === String(addTargetId));
                if (g) {
                    newGrant.targetName = g.name;
                    newGrant.groupTypeLabel = type.label;
                    break;
                }
            }
        }
        setLocalGrants((prev) => [...prev, newGrant]);
        setAddTargetId('');
        setAddRoleId('');
        setAddPerms({ canView: true, canUpdate: false, canDelete: false, canCreateChildren: false });
        setShowAddForm(false);
    }, [addType, addTargetId, addRoleId, addPerms, subjects, info]);

    const handleSave = useCallback(async () => {
        setSaving(true);
        setError('');
        try {
            const payload = {
                overrideAccess,
                accessGrants: localGrants.map((g) => ({
                    grantType: g.grantType,
                    targetId: g.targetId,
                    roleId: g.roleId || '',
                    canView: g.canView,
                    canUpdate: g.canUpdate,
                    canDelete: g.canDelete,
                    canCreateChildren: g.canCreateChildren,
                })),
            };
            const updated = await saveDirectoryAccess(directoryId, payload);
            setInfo(updated);
            setLocalGrants(updated.localGrants || []);
            setOverrideAccess(updated.overrideAccess || false);
            onSaved?.();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }, [directoryId, localGrants, overrideAccess, onSaved]);

    const hasProject = useMemo(() => !!info?.projectId, [info]);
    const isRoot = useMemo(() => info?.isRoot ?? true, [info]);
    const effectiveGrants = useMemo(() => info?.effectiveGrants || [], [info]);
    const children = useMemo(() => info?.children || [], [info]);

    if (!isActive) return null;

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 text-stone-400">
                <Loader2 size={20} className="animate-spin mr-2" /> Chargement…
            </div>
        );
    }

    if (accessDenied) {
        return (
            <div className="flex flex-col items-center justify-center py-14 text-center">
                <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mb-3">
                    <ShieldAlert size={24} className="text-amber-500" />
                </div>
                <p className="text-sm font-medium text-stone-700 mb-1">Accès restreint</p>
                <p className="text-xs text-stone-400 max-w-xs">
                    Vous n'avez pas les droits nécessaires pour consulter les accès de ce répertoire.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {!isRoot && (
                <div className="flex items-start gap-3 p-4 rounded-xl border border-stone-200 bg-stone-50">
                    <button
                        type="button"
                        onClick={() => { if (!readOnly) setOverrideAccess((v) => !v); }}
                        disabled={readOnly}
                        className={`mt-0.5 relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors ${readOnly ? 'cursor-default opacity-60' : 'cursor-pointer'} ${overrideAccess ? 'bg-amber-500' : 'bg-stone-300'}`}
                    >
                        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${overrideAccess ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-stone-800">Couper l'héritage</p>
                        <p className="text-xs text-stone-500 mt-0.5">
                            Si activé, ce répertoire n'héritera plus des droits de son parent.
                        </p>
                        {overrideAccess && (
                            <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded-lg">
                                <AlertTriangle size={12} />
                                Seuls les grants locaux ci-dessous s'appliqueront.
                            </div>
                        )}
                    </div>
                </div>
            )}

            <section>
                <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <Users size={13} /> Accès effectifs
                    <span className="text-stone-300 font-normal normal-case tracking-normal">({effectiveGrants.length})</span>
                </h3>
                {effectiveGrants.length === 0 ? (
                    <p className="text-sm text-stone-400 italic">Aucun accès défini.</p>
                ) : (
                    <div className="border border-stone-200 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-stone-50 text-stone-500 text-xs">
                                    <th className="text-left px-3 py-2 font-medium">Sujet</th>
                                    <th className="text-left px-3 py-2 font-medium w-14">Source</th>
                                    {PERM_KEYS.map((k) => (
                                        <th key={k} className="text-center px-1 py-2 font-medium w-16">{PERM_LABELS[k]}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {effectiveGrants.map((g, i) => (
                                    <tr key={i} className={g.isInherited ? 'bg-stone-50/50 text-stone-500 italic' : ''}>
                                        <td className="px-3 py-2"><GrantSubjectLabel grant={g} /></td>
                                        <td className="px-3 py-2"><InheritanceBadge inherited={g.isInherited} /></td>
                                        {PERM_KEYS.map((k) => (
                                            <td key={k} className="text-center px-1 py-2"><PermBadge granted={g[k]} /></td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold text-stone-400 uppercase tracking-widest flex items-center gap-2">
                        <Shield size={13} /> Grants locaux
                        <span className="text-stone-300 font-normal normal-case tracking-normal">({localGrants.length})</span>
                    </h3>
                    {!readOnly && (
                        <button
                            type="button"
                            onClick={() => { setShowAddForm(true); loadSubjects(); }}
                            className="btn btn-ghost btn-sm"
                        >
                            <Plus size={13} /> Ajouter un accès
                        </button>
                    )}
                </div>

                {localGrants.length > 0 && (
                    <div className="border border-stone-200 rounded-xl overflow-hidden mb-3">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-stone-50 text-stone-500 text-xs">
                                    <th className="text-left px-3 py-2 font-medium">Sujet</th>
                                    {PERM_KEYS.map((k) => (
                                        <th key={k} className="text-center px-1 py-2 font-medium w-16">{PERM_LABELS[k]}</th>
                                    ))}
                                    {!readOnly && <th className="w-10" />}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {localGrants.map((g, i) => (
                                    <tr key={i}>
                                        <td className="px-3 py-2"><GrantSubjectLabel grant={g} /></td>
                                        {PERM_KEYS.map((k) => (
                                            <td key={k} className="text-center px-1 py-2">
                                                {readOnly ? (
                                                    <PermBadge granted={g[k]} />
                                                ) : (
                                                    <input
                                                        type="checkbox"
                                                        checked={!!g[k]}
                                                        onChange={() => handleToggleLocalPerm(i, k)}
                                                        className="h-3.5 w-3.5 rounded border-stone-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                                    />
                                                )}
                                            </td>
                                        ))}
                                        {!readOnly && (
                                            <td className="text-center px-1 py-2">
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveLocal(i)}
                                                    className="p-1 rounded hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {showAddForm && (
                    <div className="border border-blue-200 rounded-xl p-4 bg-blue-50/30 space-y-3">
                        {subjectsLoading ? (
                            <div className="flex items-center gap-2 text-sm text-stone-400 py-2">
                                <Loader2 size={14} className="animate-spin" /> Chargement des sujets…
                            </div>
                        ) : (
                            <>
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <div className="flex-1">
                                        <label htmlFor={`${fieldId}-type`} className="field-label mb-1">Type</label>
                                        <select
                                            id={`${fieldId}-type`}
                                            value={addType}
                                            onChange={(e) => { setAddType(e.target.value); setAddTargetId(''); setAddRoleId(''); }}
                                            className="select"
                                        >
                                            <option value="user">Utilisateur</option>
                                            <option value="group">Groupe</option>
                                            {hasProject && <option value="project_role">Rôle du projet</option>}
                                        </select>
                                    </div>
                                    {addType === 'user' && (
                                        <div className="flex-1">
                                            <label htmlFor={`${fieldId}-user`} className="field-label mb-1">Utilisateur</label>
                                            <select
                                                id={`${fieldId}-user`}
                                                value={addTargetId}
                                                onChange={(e) => setAddTargetId(e.target.value)}
                                                className="select"
                                            >
                                                <option value="">— Sélectionner —</option>
                                                {(subjects?.users || []).map((u) => (
                                                    <option key={u.id} value={u.id}>{u.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    {addType === 'project_role' && (
                                        <div className="flex-1">
                                            <label htmlFor={`${fieldId}-role`} className="field-label mb-1">Rôle</label>
                                            <select
                                                id={`${fieldId}-role`}
                                                value={addRoleId}
                                                onChange={(e) => {
                                                    setAddRoleId(e.target.value);
                                                    if (subjects?.projectId) setAddTargetId(String(subjects.projectId));
                                                }}
                                                className="select"
                                            >
                                                <option value="">— Sélectionner —</option>
                                                {(subjects?.availableRoles || []).map((r) => (
                                                    <option key={r.id} value={r.id}>{r.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                    {addType === 'group' && (
                                        <div className="flex-1">
                                            <label htmlFor={`${fieldId}-group`} className="field-label mb-1">Groupe</label>
                                            <select
                                                id={`${fieldId}-group`}
                                                value={addTargetId}
                                                onChange={(e) => setAddTargetId(e.target.value)}
                                                aria-describedby={`${fieldId}-group-help`}
                                                className="select"
                                            >
                                                <option value="">— Sélectionner —</option>
                                                {(subjects?.groupTypes || []).map((type) => (
                                                    <optgroup key={type.id} label={type.label}>
                                                        {type.groups.map((g) => (
                                                            <option key={g.id} value={g.id}>{g.name}</option>
                                                        ))}
                                                    </optgroup>
                                                ))}
                                            </select>
                                            <p id={`${fieldId}-group-help`} className="field-hint mt-1">
                                                {(subjects?.groupTypes || []).length
                                                    ? 'Tout membre du groupe, quel que soit son rôle.'
                                                    : 'Aucun groupe visible avec vos droits.'}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <fieldset>
                                    <legend className="field-label">Permissions</legend>
                                    <div className="flex flex-wrap gap-3 mt-1.5">
                                        {PERM_KEYS.map((k) => (
                                            <label key={k} className="inline-flex items-center gap-1.5 text-sm text-stone-700 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={addPerms[k]}
                                                    onChange={() => setAddPerms((p) => ({ ...p, [k]: !p[k] }))}
                                                    className="h-3.5 w-3.5 rounded border-stone-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                {PERM_LABELS[k]}
                                            </label>
                                        ))}
                                    </div>
                                </fieldset>
                                <div className="flex justify-end gap-2 pt-1">
                                    <button type="button" onClick={() => setShowAddForm(false)} className="btn btn-secondary btn-sm">
                                        Annuler
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleAddGrant}
                                        disabled={addType === 'project_role' ? !addRoleId : !addTargetId}
                                        className="btn btn-primary btn-sm"
                                    >
                                        Ajouter
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </section>

            {children.length > 0 && (
                <section>
                    <button
                        type="button"
                        onClick={() => setChildrenOpen((v) => !v)}
                        className="flex items-center gap-2 text-xs font-semibold text-stone-400 uppercase tracking-widest hover:text-stone-600 transition-colors mb-3"
                    >
                        {childrenOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                        <Folder size={13} />
                        Sous-répertoires ({children.length})
                    </button>
                    {childrenOpen && (
                        <div className="space-y-2">
                            {children.map((child) => (
                                <div key={child.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-stone-200 bg-white">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <Folder size={14} className="text-stone-400 shrink-0" aria-hidden="true" />
                                        <span className="text-sm font-medium text-stone-700 truncate">{child.name}</span>
                                    </div>
                                    <ChildStatusBadge child={child} />
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {error && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{error}</p>
            )}

            {!readOnly && (
                <div className="flex justify-end gap-3 pt-2">
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="btn btn-primary flex items-center gap-2"
                    >
                        {saving && <Loader2 size={14} className="animate-spin" />}
                        Enregistrer les accès
                    </button>
                </div>
            )}
        </div>
    );
}
